import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, FileType, AlertTriangle, ShieldCheck } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import CoBrandStrip from "@/components/CoBrandStrip";
import ResumeSection from "@/components/resume/ResumeSection";
import SkillsSection from "@/components/resume/SkillsSection";
import ExperienceSection from "@/components/resume/ExperienceSection";
import EducationSection from "@/components/resume/EducationSection";
import {
  ProjectsSection,
  CertificationsSection,
  LanguagesSection,
} from "@/components/resume/MiscSections";
import {
  getSharedResume,
  sharedPdfUrl,
  sharedDocxUrl,
  logoUrl,
} from "@/lib/api";

export default function SharedResume() {
  const { token } = useParams();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getSharedResume(token)
      .then((data) => {
        if (!cancelled) setResume(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Link unavailable");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner-dark" />
      </div>
    );
  }

  if (error || !resume) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div
          className="swiss-card p-8 max-w-md text-center"
          data-testid="share-error-card"
        >
          <AlertTriangle
            className="w-10 h-10 text-[var(--brand-warning)] mx-auto mb-3"
            strokeWidth={1.5}
          />
          <h1 className="font-heading text-2xl font-bold tracking-tight mb-2">
            LINK UNAVAILABLE
          </h1>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {error ||
              "This share link is no longer valid. It may have expired or been revoked."}
          </p>
        </div>
      </div>
    );
  }

  const pdfHref = sharedPdfUrl(token);
  const docxHref = sharedDocxUrl(token);

  return (
    <div className="min-h-screen">
      {/* Slim public header */}
      <header
        className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-[var(--border-default)]"
        data-testid="share-header"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-3.5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <BrandLogo size={32} />
            <div className="leading-none">
              <div className="font-heading font-bold text-[15px] tracking-tight text-[var(--text-primary)]">
                ITHR <span className="gradient-text">TalentLens</span>
              </div>
              <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--text-muted)] mt-1">
                Shared candidate profile
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={pdfHref}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="share-download-pdf"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              PDF
            </a>
            <a
              href={docxHref}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary"
              data-testid="share-download-docx"
            >
              <FileType className="w-4 h-4" strokeWidth={1.5} />
              DOCX
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 md:px-12 py-10">
        <div className="mb-4 flex items-center gap-2 text-xs text-[var(--text-muted)]">
          <ShieldCheck
            className="w-3.5 h-3.5 text-[var(--brand-success)]"
            strokeWidth={2}
          />
          <span>
            Contact information automatically scrubbed · GDPR-friendly preview
          </span>
        </div>

        <div
          className="bg-white border border-[var(--border-default)] hard-shadow p-8 md:p-14 max-w-[920px] mx-auto fade-in"
          data-testid="shared-resume-paper"
        >
          {/* Top: name + logo */}
          <div className="flex items-start justify-between pb-5 border-b border-[var(--border-default)] mb-8">
            <div className="flex-1 pr-6">
              <h1
                className="font-heading text-3xl md:text-5xl font-bold tracking-tight leading-none uppercase mb-2 text-[var(--text-primary)]"
                data-testid="shared-name"
              >
                {resume.full_name || "Candidate Profile"}
              </h1>
              {resume.professional_title && (
                <p
                  className="text-base text-[var(--text-secondary)] font-normal"
                  data-testid="shared-title"
                >
                  {resume.professional_title}
                </p>
              )}
            </div>
            <div
              className="w-28 h-20 md:w-36 md:h-24 border border-[var(--border-default)] flex items-center justify-center shrink-0 overflow-hidden bg-white p-2"
              data-testid="shared-logo"
            >
              <img
                src={logoUrl()}
                alt="company logo"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>

          {resume.summary && (
            <ResumeSection title="Summary" index={1}>
              <p className="text-[15px] leading-relaxed text-[var(--text-primary)]">
                {resume.summary}
              </p>
            </ResumeSection>
          )}

          {(resume.skills || []).length > 0 && (
            <SkillsSection skills={resume.skills} editing={false} />
          )}
          {(resume.experience || []).length > 0 && (
            <ExperienceSection experience={resume.experience} editing={false} />
          )}
          {(resume.education || []).length > 0 && (
            <EducationSection education={resume.education} editing={false} />
          )}
          <ProjectsSection projects={resume.projects || []} />
          <CertificationsSection certifications={resume.certifications || []} />
          <LanguagesSection languages={resume.languages || []} />

          <div className="mt-8 pt-4 border-t border-[var(--border-default)] flex flex-col md:flex-row justify-between items-start md:items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
            <span>Shared preview · contact data scrubbed</span>
            <span className="text-[var(--text-secondary)] font-sans text-[10pt] normal-case tracking-normal">
              ITHR Technologies Consulting LLC
            </span>
          </div>
        </div>
      </main>
      <CoBrandStrip className="mt-16" />
    </div>
  );
}
